import { Dialog, DialogTitle, Stack, TextField, DialogContent, 
  Box, DialogActions, FormControl, InputLabel, MenuItem, Select, Typography, 
  FormControlLabel,
  SelectChangeEvent} 
  from "@mui/material";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import * as React from "react";
import * as ATCAConstants from '../util/ATCAConstants'
import * as Utils from '../util/Utils'
import {ScheduleContext} from "../schedule/ScheduleContext";
import {ProjectContext} from "../schedule/ProjectContext";

const UPDATE_FIELDS: any = {
  "duration": "Duration (seconds)",
  "pointing": "Pointing",
  "scanType": "Scan Type",
  "correlator_configuration": "Correlator Configuration"
};

const ITEM_HEIGHT = 35;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 12 + ITEM_PADDING_TOP,
      // width: 250,
    },
  },
};

export default function BulkScanEditorDialog(
  props: {
    open: boolean,
    handleUpdate: (selectedScans: any[], setting: any) => void,
    handleClose: () => void,
  }
) {
  const scheduleContext = React.useContext(ScheduleContext);
  const projectContext = React.useContext(ProjectContext);
  const [disableUpdate, setDisableUpdate] = React.useState(true);
  const [scans, setScans] = React.useState<any[]>([]);
  const [selectedIndex, setSelectedIndex] = React.useState<number[]>([]);
  const [scanSetting, setScanSetting] = React.useState<any>(ATCAConstants.DEFAULT_SETTING);
  const [updateFields, setUpdateFields] = React.useState<any[]>([
    "duration", "correlator_configuration", "pointing", "scanType"
  ]);

  React.useEffect(() => {
    if (props.open) {
      setScans(scheduleContext.getScans());
      setSelectedIndex([]);
      setScanSetting(ATCAConstants.DEFAULT_SETTING);
    }
  }, [props.open]);

  React.useEffect(() => {
    if ((selectedIndex.length === 0) || 
      (updateFields.length === 0) ){
      setDisableUpdate(true);
      return;
    }
    for (let fieldName of updateFields) {
      if (!scanSetting[fieldName]) {
        setDisableUpdate(true);
        return;
      }
    }
    setDisableUpdate(false);
  }, [selectedIndex, scanSetting, updateFields]);

  const getScanDetails = (scan: any) => {
    const targetList = projectContext.getTargets();
    const targets = targetList.filter((t: any) => (t['name'] === scan['name']));
    if (targets.length === 0) {
      return '';
    }
    const target = targets[0];

    const fullScan = {
      ...target,
      ...scan
    };

    return `${fullScan['ra']} ${fullScan['dec']} ${fullScan['coord_sys']||'J2000'} ${Utils.getTargetVelocityString(fullScan)}`;
  }

  const getSelectedScans = () => {
    const selectedScans: any[] = [];
    for (const i of selectedIndex) {
      const selectedScan = scans[i];
      selectedScans.push(selectedScan);
    }

    return selectedScans;
  }

  const toggleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIndex(Array.from(scans.keys()));
    } else {
      setSelectedIndex([]);
    }
  };

  const toggleSelection = (index: number) => {
    let newSelections = [...selectedIndex];
    const i = selectedIndex.indexOf(index);
    if (i < 0) {
      newSelections.push(index);
    } else {
      newSelections.splice(i, 1);
    }

    setSelectedIndex(newSelections.sort((n1, n2) => n1 - n2));
  }

  const handleUpdateFields = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const fields: string[] = (typeof value === 'string') ? value.split(',') : value;
    setUpdateFields(fields);

    const setting = {...scanSetting};
    for (let field of Object.keys(UPDATE_FIELDS)) {
      if (fields.includes(field)) {
        if (!Object.keys(scanSetting).includes(field)) {
          setting[field] = ATCAConstants.DEFAULT_SETTING[field];
        }
      } else {
        delete setting[field];
      }
    }
    setScanSetting(setting);
  }

  const setScanValue = (fieldName: string, value: any) => {
    setScanSetting({
      ...scanSetting,
      [fieldName]: value
    });
  }

  function renderCorrelatorConfig(config: any): React.ReactNode {
    const setting = projectContext.getCorrelatorSetting(
      config['correlator_setting'], config['frequency_configuration']['receiver']);
    const freqConfig = setting['frequency_configuration'] || {};

    return (<Stack direction="row" spacing={2}>
      <Typography variant="body2">
        {freqConfig['receiver']}
      </Typography>
      <Typography variant="body2">
        {(setting['spectral_points_per_subband']
          * setting['subbands_per_IF']
          * setting['number_of_IF'])} points
      </Typography>

      <Typography variant="body2">
        {(config['sub_band_configuration'] || []).length} zooms
      </Typography>

      <Typography variant="body2">
        {projectContext.calculateDataRate(
          config['name'], freqConfig['receiver'])} kB/s
      </Typography>
    </Stack>);
  }

  return (
    <Dialog open={props.open} PaperProps={{ sx: {height: '70%'} }}
      aria-labelledby="update-source-dialog-title"
      onClose={props.handleClose}
      maxWidth="md" fullWidth>
      <DialogTitle id="update-source-dialog-title">
        Select scans to update
      </DialogTitle>
      <DialogContent>
        <Stack flexDirection={'row'} height={'70px'} marginBottom={'15px'}
          alignItems={'center'} justifyContent={'space-between'}>

          <FormControlLabel
            sx={{
              marginLeft: '10px',
              '& .MuiFormControlLabel-label': {color: '#004B87'}
            }}
            control={
              <Checkbox color="primary"
                onChange={toggleSelectAll}
                checked={selectedIndex.length === scans.length && scans.length !== 0}
                indeterminate={
                  selectedIndex.length !== scans.length && selectedIndex.length !== 0
                }
                disabled={scans.length === 0}
                inputProps={{
                  'aria-label': 'Select/Unselect all items',
                }}
              />
            } 
            label={`Select/Unselect all items (${selectedIndex.length} selected)`}
          />
          <FormControl sx={{ width: 'calc(60% - 32px)'}}>
            <InputLabel id="update-fields-label">Fields to Update</InputLabel>
            <Select
              labelId="update-fields-label"
              id="update-fields"
              label="Fields to Update"
              displayEmpty={true}
              multiple
              value={updateFields}
              onChange={handleUpdateFields}
              renderValue={(selected) => selected.join(', ')}
              MenuProps={MenuProps}
            >
              {Object.keys(UPDATE_FIELDS).map((key) => (
                <MenuItem key={key} value={key}>
                  <Checkbox checked={updateFields.includes(key)} />
                  <ListItemText primary={UPDATE_FIELDS[key]}/>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction={'row'} height={'calc(100% - 85px)'} spacing= {4}>
          <Box sx={{
            height: '100%',
            width: '40%', border: '1px solid #eeeeee',
          }}>
            <List sx={{
              padding: 0, height: '100%', overflowY: 'scroll'
            }}>
              {scans.map((scan: any, index) => (
                <ListItem key={`scan-${index}`}
                  role="listitem"
                  onClick={e => toggleSelection(index)}
                  sx={{
                    padding: '10px',
                    borderBottom: '1px solid #eeeeee',
                    bgcolor: (selectedIndex.indexOf(index) >= 0) ? '#e0f7fa' : '',
                    "&:hover": {
                      cursor: 'pointer',
                      bgcolor: '#f5f5f5',
                    }
                  }}>
                  <ListItemIcon>
                    <Checkbox
                      id={`scan-${index}-select`}
                      checked={selectedIndex.indexOf(index) >= 0}
                      disableRipple
                    />
                  </ListItemIcon>

                  <ListItemText 
                    primary={
                      <div style={{display: 'flex'}}>
                        <Typography variant="body1">
                          {scan['name']}
                        </Typography>
                      </div>
                    }
                    secondary={
                      <Typography variant="body2">
                        {getScanDetails(scan)}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <Stack spacing={4} flexGrow={4} justifyContent={'center'}>

            {
                updateFields.includes('duration') &&
                <TextField id="duration" label="Duration (seconds)"
                error={!scanSetting['duration']}
                value={scanSetting['duration'] || 0}
                onChange={(e) => setScanValue('duration', Number(e.target.value))}
                variant="standard" />
            }

            
            {
              updateFields.includes('scanType') &&
              <FormControl>
                <InputLabel id="scan-type-label">Scan Type</InputLabel>
                <Select
                  labelId="scan-type-label"
                  id="scan-type"
                  label="Scan Type"
                  displayEmpty={true}
                  onChange={e => setScanValue('scanType', e.target.value as string)}
                  value={scanSetting['scanType'] || ''}
                >
                  {
                    ATCAConstants.SCAN_TYPES.map((type) => (
                      <MenuItem key={type} value={type} disabled={ATCAConstants.DISABLE_SCAN_TYPES.includes(type)}>
                        {type}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            }

            {
              updateFields.includes('pointing') &&
              <FormControl>
                <InputLabel id="pointing-label">Pointing</InputLabel>
                <Select
                  labelId="pointing-label"
                  id="pointing"
                  label="Pointing"
                  displayEmpty={true}
                  onChange={e => setScanValue('pointing', e.target.value as string)}
                  value={scanSetting['pointing'] || ''}
                >
                  {
                    ATCAConstants.POINTINGS.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            }

            {
              updateFields.includes('correlator_configuration') &&
              <FormControl error={!scanSetting['correlator_configuration']}>
                <InputLabel id="corr-config-label">Correlator Configuration</InputLabel>
                <Select
                  labelId="corr-config-label"
                  id="corr-config"
                  label="Correlator Configuration"
                  displayEmpty={true}
                  renderValue={(selected) => selected}
                  onChange={e => setScanValue('correlator_configuration', e.target.value as string)}
                  value={scanSetting['correlator_configuration'] || ''}
                >
                  {
                    projectContext.correlatorConfigurations.map((config, index) => (
                      <MenuItem key={config['name'] + '-' + index} value={config['name']}>
                        <ListItemText primary={config['name']}
                          secondary={renderCorrelatorConfig(config)}
                        />
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            }
              
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button autoFocus
          disabled={disableUpdate}
          onClick={e => props.handleUpdate(getSelectedScans(), scanSetting)}
        >
          Update
        </Button>
        <Button onClick={props.handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
